import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEllipsisV, FaFlag, FaUserTimes } from 'react-icons/fa';
import ReportUserModal from './ReportUserModal';
import './UserActionsDropdown.css';

/**
 * Reusable dropdown component for user actions (report, block, etc.)
 * Can be integrated anywhere a user profile is displayed
 * 
 * @param {Object} props
 * @param {string} props.userId - ID of the user to perform actions on
 * @param {string} props.userName - Display name of the user
 * @param {Object} props.context - Context for the report (post, package, conversation)
 * @param {string} props.size - Size: 'small', 'medium', 'large'
 * @param {string} props.variant - Style variant: 'default', 'minimal', 'outlined'
 * @param {boolean} props.disabled - Whether the dropdown is disabled
 * @param {Array} props.hiddenActions - Array of action names to hide
 */
const UserActionsDropdown = ({
  userId,
  userName = 'User',
  context = {},
  size = 'medium',
  variant = 'default',
  disabled = false,
  hiddenActions = []
}) => {
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Don't render if no userId provided
  if (!userId) {
    return null;
  }

  const handleToggleDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleReportUser = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(false);
    setShowReportModal(true);
  };

  const handleBlockUser = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(false);
    // TODO: Implement block user functionality
    console.log('Block user functionality to be implemented');
  };

  const handleReportSubmitted = () => {
    setShowReportModal(false);
    // Could trigger a refresh or show success message
  };

  // Filter available actions based on hiddenActions prop
  const availableActions = [
    {
      id: 'report',
      label: t('actions.report_user'),
      icon: <FaFlag />,
      onClick: handleReportUser,
      className: 'report-action'
    },
    {
      id: 'block',
      label: t('actions.block_user'),
      icon: <FaUserTimes />,
      onClick: handleBlockUser,
      className: 'block-action'
    }
  ].filter(action => !hiddenActions.includes(action.id));

  // Don't render if no actions available
  if (availableActions.length === 0) {
    return null;
  }

  return (
    <>
      <div 
        className={`user-actions-dropdown2 ${variant} ${size} ${disabled ? 'user-actions-disabled' : ''}`}
        ref={dropdownRef}
      >
        <button
          type="button"
          className="user-actions-trigger"
          onClick={handleToggleDropdown}
          disabled={disabled}
          aria-label={t('actions.user_actions')}
          title={t('actions.user_actions')}
        >
          <FaEllipsisV />
        </button>

        {isOpen && (
          <div className="user-actions-menu">
            <div className="user-actions-header">
              <span className="user-actions-title">{t('actions.user_actions')}</span>
            </div>
            
            <div className="user-actions-list">
              {availableActions.map((action) => (
                <button
                  key={action.id}
                  type="button"
                  className={`user-actions-item ${action.className}`}
                  onClick={action.onClick}
                >
                  <span className="user-action-icon">{action.icon}</span>
                  <span className="user-action-label">{action.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Report Modal */}
      <ReportUserModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        userId={userId}
        userName={userName}
        context={context}
        onReportSubmitted={handleReportSubmitted}
      />
    </>
  );
};

export default UserActionsDropdown;